import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { BookOpen, Eye, Lightbulb, Activity, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import CockpitHeatmap from "@/components/CockpitHeatmap";
import CockpitTimeline from "@/components/CockpitTimeline";
import SavedLibrary from "@/components/SavedLibrary";

// Sprint 8.B — Detail-Mode: volle Sicht aller Cockpit-Sektionen + SavedLibrary inline.
// Nur für Pro-User sichtbar.

export default function DetailMode({ user, burner, previews, isPro }) {
  const navigate = useNavigate();
  const [savedItems, setSavedItems] = useState([]);
  const fokustyp = burner?.primary_fokustyp || previews?.primary_fokustyp;

  useEffect(() => {
    api
      .get("/anliegen/saved?limit=50")
      .then((r) => setSavedItems(r.data?.items || []))
      .catch(() => setSavedItems([]));
  }, []);

  return (
    <div data-testid="detail-mode" className="space-y-7">
      {/* Aktivität (voll) */}
      <motion.section
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        data-testid="detail-section-activity"
        className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-sm"
      >
        <div className="flex items-center gap-2 mb-4">
          <Activity className="w-5 h-5 text-[#4563BB]" />
          <h2 className="text-lg font-extrabold tracking-tight text-[var(--fts-text-primary)]">
            Alle {burner?.bruecken_count ?? 0} Flexpfade
          </h2>
        </div>
        <SavedLibrary user={user} />
      </motion.section>

      {/* Muster (voll mit Heatmap groß) */}
      <motion.section
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.05 }}
        data-testid="detail-section-muster"
        className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-sm"
      >
        <div className="flex items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-2">
            <Eye className="w-5 h-5 text-[#4563BB]" />
            <h2 className="text-lg font-extrabold tracking-tight text-[var(--fts-text-primary)]">
              Alle {burner?.muster_count ?? 0} Konflikt-Muster
            </h2>
          </div>
          {burner?.top_muster && (
            <span className="text-xs font-bold text-slate-500">
              Top: {burner.top_muster.name} · {burner.top_muster.count} Flexpfade
            </span>
          )}
        </div>
        {burner?.heatmap ? (
          <CockpitHeatmap
            data={burner.heatmap}
            isPro={isPro}
            topMuster={burner.top_muster}
          />
        ) : (
          <p className="text-sm text-slate-500 italic">Keine Daten.</p>
        )}
      </motion.section>

      {/* Vorausschau (Timeline gross) */}
      <motion.section
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        data-testid="detail-section-timeline"
        className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-sm"
      >
        <div className="flex items-center gap-2 mb-4">
          <BookOpen className="w-5 h-5 text-[#4563BB]" />
          <h2 className="text-lg font-extrabold tracking-tight text-[var(--fts-text-primary)]">
            Deine Flexpfad-Reise im Detail
          </h2>
        </div>
        {burner?.timeline ? (
          <CockpitTimeline data={burner.timeline} />
        ) : (
          <p className="text-sm text-slate-500 italic">Keine Daten.</p>
        )}
      </motion.section>

      {/* Hot Tips (alle als Cards) */}
      <motion.section
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.15 }}
        data-testid="detail-section-tips"
        className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-sm"
      >
        <div className="flex items-center gap-2 mb-4">
          <Lightbulb className="w-5 h-5 text-[#4563BB]" />
          <h2 className="text-lg font-extrabold tracking-tight text-[var(--fts-text-primary)]">
            Alle {(previews?.hot_tips || []).length || 8} Hot Tips
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {(previews?.hot_tips || []).map((t, i) => (
            <div
              key={i}
              data-testid={`detail-tip-card-${i}`}
              className="rounded-xl border border-slate-100 bg-slate-50 p-4"
            >
              <p className="text-sm font-bold text-slate-900">
                #{i + 1} {t.title}
              </p>
              {t.insight && (
                <p className="mt-1 text-xs text-slate-600 leading-relaxed">{t.insight}</p>
              )}
              <button
                type="button"
                onClick={() => navigate("/dashboard/anliegen")}
                data-testid={`detail-tip-action-${i}`}
                className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-[#4563BB] hover:underline"
              >
                Beispiel-Flexpfad erstellen <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      </motion.section>
    </div>
  );
}
